import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { Combobox, ComboboxHeading, ComboboxOption } from '@clay-run/glaze/combobox';
import { Text } from '@clay-run/glaze/text';
import { AutofillIgnore } from '../../../ui/AutofillIgnore';
import { sectionOptions, type SectionableOption } from './postingSections';

export interface NotesSectionedPickerProps<T extends SectionableOption> {
  options: T[];
  /** The selected option, or null when none of `options` is selected. */
  selected: T | null;
  onSelect: (option: T) => void;
  'aria-label': string;
  placeholder: string;
  searchPlaceholder: string;
  popupWidth: 'trigger' | 'full';
  className?: string;
  /** The trigger's content for the selected option. */
  renderValue: (option: T) => ReactNode;
  /** One row of the list. */
  renderRow: (option: T) => ReactNode;
  /** Shown under the list (e.g. a "more not shown" hint), given the number of rows hidden until a search. */
  footer?: (hiddenUntilSearch: number) => ReactNode;
}

/**
 * The shared shell of the two Notes pickers ("Posting to" and the People filter): a Glaze
 * `Combobox variant="popover"` with a search box, Account/People section headings in registry order
 * (`postingSections.ts`), "No matches", and a footer slot. Filtering is ours (Glaze's combobox
 * filters nothing). Callers only build options and say how a row and the trigger look.
 */
export function NotesSectionedPicker<T extends SectionableOption>({
  options,
  selected,
  onSelect,
  'aria-label': ariaLabel,
  placeholder,
  searchPlaceholder,
  popupWidth,
  className,
  renderValue,
  renderRow,
  footer,
}: NotesSectionedPickerProps<T>) {
  const [query, setQuery] = useState('');
  const { sections, flat, hiddenNoNotes } = useMemo(() => sectionOptions(options, query), [options, query]);

  return (
    <AutofillIgnore>
      <Combobox<T>
        variant="popover"
        size="sm"
        popupWidth={popupWidth}
        by="key"
        aria-label={ariaLabel}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        hasClearButton={false}
        className={className}
        items={flat}
        itemToStringLabel={o => o.label}
        value={selected}
        renderValue={renderValue}
        onValueChange={item => {
          if (!item) return;
          onSelect(item);
          setQuery('');
        }}
        inputValue={query}
        onInputValueChange={setQuery}
      >
        {flat.length === 0 ? (
          <Text size="xs" variant="tertiary" className="block px-2.5 py-1.5">
            No matches
          </Text>
        ) : (
          sections.map(section => (
            <Fragment key={section.key}>
              <ComboboxHeading>{section.heading}</ComboboxHeading>
              {section.options.map(o => (
                <ComboboxOption key={o.key} value={o} aria-label={o.label}>
                  {renderRow(o)}
                </ComboboxOption>
              ))}
            </Fragment>
          ))
        )}
        {footer?.(hiddenNoNotes)}
      </Combobox>
    </AutofillIgnore>
  );
}
